export type LensMapOptions = {
  /** largura do mapa em px */
  width: number
  /** altura do mapa em px */
  height: number
  /** raio do canto arredondado, em px */
  radius: number
  /** largura da faixa que refrata, medida para dentro da borda */
  bevel: number
  /** 0 = neutro, 1 = deslocamento máximo */
  strength: number
}

/**
 * Distância com sinal até a borda de um retângulo arredondado.
 * Negativa dentro, positiva fora. Origem no centro.
 */
function roundRectSignedDistance(
  px: number,
  py: number,
  halfWidth: number,
  halfHeight: number,
  cornerRadius: number,
): number {
  const qx = Math.abs(px) - halfWidth + cornerRadius
  const qy = Math.abs(py) - halfHeight + cornerRadius
  const outside = Math.hypot(Math.max(qx, 0), Math.max(qy, 0))
  return Math.min(Math.max(qx, qy), 0) + outside - cornerRadius
}

/**
 * Mapa de deslocamento para feDisplacementMap.
 * R codifica o deslocamento em x, G em y; 128 é neutro.
 *
 * O miolo fica neutro e só a faixa de `bevel` deforma, empurrando para
 * fora — que é o que faz o fundo parecer comprimido junto à borda, como
 * numa lente de verdade.
 */
// Anotado com <ArrayBuffer> (não o bare `Uint8ClampedArray`, que o lib.dom
// do TypeScript trata como <ArrayBufferLike>): sem isso, o retorno não
// entra direto no construtor de ImageData em lensMapDataUrl, que exige um
// typed array apoiado especificamente em ArrayBuffer, não SharedArrayBuffer.
export function buildLensMap(options: LensMapOptions): Uint8ClampedArray<ArrayBuffer> {
  const { width, height, radius, bevel, strength } = options
  const data = new Uint8ClampedArray(width * height * 4)

  const halfWidth = width / 2
  const halfHeight = height / 2
  const cornerRadius = Math.min(radius, halfWidth, halfHeight)
  const epsilon = 1

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const px = x + 0.5 - halfWidth
      const py = y + 0.5 - halfHeight
      const distance = roundRectSignedDistance(px, py, halfWidth, halfHeight, cornerRadius)

      let dx = 0
      let dy = 0

      // Só a faixa interna junto à borda refrata.
      if (bevel > 0 && strength > 0 && distance < 0 && distance > -bevel) {
        // t: 0 no fim da faixa, 1 encostado na borda.
        const t = 1 + distance / bevel
        // Perfil suave; a curva concentra o efeito bem na borda.
        const weight = t * t * strength

        // Normal da superfície = gradiente da SDF, apontando para fora.
        const gx =
          roundRectSignedDistance(px + epsilon, py, halfWidth, halfHeight, cornerRadius) -
          roundRectSignedDistance(px - epsilon, py, halfWidth, halfHeight, cornerRadius)
        const gy =
          roundRectSignedDistance(px, py + epsilon, halfWidth, halfHeight, cornerRadius) -
          roundRectSignedDistance(px, py - epsilon, halfWidth, halfHeight, cornerRadius)
        const norm = Math.hypot(gx, gy) || 1

        dx = (gx / norm) * weight
        dy = (gy / norm) * weight
      }

      const i = (y * width + x) * 4
      data[i] = 128 + dx * 127
      data[i + 1] = 128 + dy * 127
      data[i + 2] = 128
      data[i + 3] = 255
    }
  }

  return data
}

/** Rasteriza o mapa num data URL de PNG, para o feImage consumir. */
export function lensMapDataUrl(options: LensMapOptions): string {
  const canvas = document.createElement('canvas')
  canvas.width = options.width
  canvas.height = options.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.putImageData(new ImageData(buildLensMap(options), options.width, options.height), 0, 0)
  return canvas.toDataURL('image/png')
}
